import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";

/**
 * WX StateContainer — unified Empty / Error wrappers.
 *
 * Usage:
 *   <EmptyState icon={Inbox} title="No domains yet" description="..." action={{label:"Add domain", onClick:...}} />
 *   <ErrorState title="Could not reach the agent" description={err.message} onRetry={reload} />
 *
 * Loading uses <Skeleton*> primitives directly (see Skeleton.jsx) — a one-size-fits-all
 * loading spinner here hides the shape of the real layout, which the blueprint
 * explicitly discourages.
 */

export const EmptyState = ({
  icon: Icon = Inbox,
  title = "Nothing here yet",
  description,
  action,
  compact = false,
  testId = "wx-empty-state",
}) => (
  <div
    className={`mx-auto flex max-w-md flex-col items-center rounded-2xl border border-dashed border-slate-800 bg-[#0B1120]/50 text-center ${compact ? "px-5 py-10 space-y-3" : "px-6 py-16 space-y-4"}`}
    data-testid={testId}
  >
    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-slate-700/50 bg-slate-800/60 text-cyan-400">
      <Icon className="h-7 w-7" />
    </div>
    <h3 className="text-lg font-bold text-slate-200">{title}</h3>
    {description && <p className="text-sm leading-relaxed text-slate-400">{description}</p>}
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="wx-btn-primary mt-2 text-sm"
        data-testid={`${testId}-action`}
      >
        {action.icon ? <action.icon className="h-4 w-4" /> : null}
        {action.label}
      </button>
    )}
  </div>
);

export const ErrorState = ({
  title = "Something went wrong",
  description,
  onRetry,
  compact = false,
  testId = "wx-error-state",
}) => (
  <div
    className={`mx-auto flex max-w-lg flex-col items-center rounded-2xl border border-rose-900/50 bg-rose-950/20 text-center ${compact ? "px-5 py-8 space-y-3" : "px-6 py-12 space-y-4"}`}
    data-testid={testId}
  >
    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300">
      <AlertTriangle className="h-6 w-6" />
    </div>
    <h3 className="text-base font-bold text-rose-100">{title}</h3>
    {description && <p className="text-sm leading-relaxed text-rose-300/80">{description}</p>}
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="wx-btn inline-flex items-center gap-2 border border-rose-500/50 bg-rose-500/15 px-4 py-2 text-xs font-mono uppercase tracking-wider text-rose-200 hover:bg-rose-500/25"
        data-testid={`${testId}-retry`}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Retry
      </button>
    )}
  </div>
);

/**
 * Inline alert — compact, for in-form or in-card warnings.
 * Variants: info | warning | danger | success
 */
export const InlineAlert = ({ variant = "info", title, children, action, testId = "wx-inline-alert" }) => {
  const palette = {
    info: "border-cyan-500/30 bg-cyan-500/10 text-cyan-100",
    warning: "border-amber-500/30 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/40 bg-rose-500/10 text-rose-100",
    success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
  }[variant];
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${palette}`} data-testid={testId} role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-none opacity-80" />
      <div className="flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="mt-0.5 text-xs leading-relaxed opacity-90">{children}</div>}
      </div>
      {action && (
        <button type="button" onClick={action.onClick} className="text-xs font-mono uppercase tracking-wider underline-offset-2 hover:underline" data-testid={`${testId}-action`}>
          {action.label}
        </button>
      )}
    </div>
  );
};
